import { createContext, useMemo, ReactNode } from 'react';
import { calculateAverageGeneric, calculateAverageMonthlyFeeSqm, calculateAverageDateTimestamp, calculatePriceTaxationRatioAverage,  } from '@/components/table/helpers';
import { useProjectContext } from './ProjectContext';
import {SalesSummaryResponseType, AdsSummaryResponseType} from '@/types/apiTypes';
import { AdsTableType, SalesRefTableType } from '@/types/dataTypes';

interface TableCalculationsContextType {
  soldCalculations: SalesSummaryResponseType | null;
  adsCalculations: AdsSummaryResponseType | null;
  selectedSoldTableData: SalesRefTableType[] | undefined;
  selectedAdsTableData: AdsTableType[] | undefined;
}

const TableCalculationsContext = createContext<TableCalculationsContextType | null>(null);

export { TableCalculationsContext };

interface Props {
  children: ReactNode;
}

export const TableCalculationsProvider = ({ children }: Props) => {
  const { project } = useProjectContext();
  const { soldTableData, adsTableData, soldTableIds, adsTableIds } = project;
  const selectedSoldTableData = soldTableData?.filter((item) => soldTableIds?.selected?.has(item.id))
  const selectedAdsTableData = adsTableData?.filter((item) => adsTableIds?.selected?.has(item.id));
  
  const soldCalculations = useMemo((): SalesSummaryResponseType | null => {
    if (selectedSoldTableData) {
      return {
        average_distance: calculateAverageGeneric(selectedSoldTableData, 'relevance'),
        average_contract_date: calculateAverageDateTimestamp(selectedSoldTableData, 'contractDate'),
        average_price: calculateAverageGeneric(selectedSoldTableData, 'soldPrice') ?? 0,
        average_living_area: calculateAverageGeneric(selectedSoldTableData, 'livingArea'),
        average_rooms: calculateAverageGeneric(selectedSoldTableData, 'rooms'),
        average_construction_year: calculateAverageGeneric(selectedSoldTableData, 'constructionYear'),
        average_plot_area: calculateAverageGeneric(selectedSoldTableData, 'plotArea')?.toString() || null,
        average_ancillary_area: calculateAverageGeneric(selectedSoldTableData, 'ancillaryArea'),
        average_price_sqm: calculateAverageGeneric(selectedSoldTableData, 'soldPriceSqm'),
        average_price_today: calculateAverageGeneric(selectedSoldTableData, 'recountedPrice') ?? 0,
        average_price_today_sqm: calculateAverageGeneric(selectedSoldTableData, 'recountedPriceSqm') ?? 0,
        average_taxation_value: calculateAverageGeneric(selectedSoldTableData, 'taxationValue'),
        average_taxation_year: calculateAverageGeneric(selectedSoldTableData, 'taxationYear') || null,
        average_price_taxation_ratio: calculatePriceTaxationRatioAverage(selectedSoldTableData, 'priceTaxationRatio') || null,
        average_monthly_fee: calculateAverageGeneric(selectedSoldTableData, 'monthlyFee'),
        average_monthly_fee_sqm: calculateAverageGeneric(selectedSoldTableData, 'monthlyFeeSqm'),
        average_registered_date: calculateAverageDateTimestamp(selectedSoldTableData, 'adRegisteredDate'),
        average_ad_registered_date: calculateAverageDateTimestamp(selectedSoldTableData, 'adRegisteredDate') || null,
        average_ad_delete_date: calculateAverageDateTimestamp(selectedSoldTableData, 'ad_delete_date') || null,
        average_ad_advertising_days: calculateAverageGeneric(selectedSoldTableData, 'adAdvertisingDays'),
        average_ad_price: calculateAverageGeneric(selectedSoldTableData, 'adPrice'),
        average_ad_price_sqm: calculateAverageGeneric(selectedSoldTableData, 'adPriceSqm'),
        average_construction_value_year: calculateAverageGeneric(selectedSoldTableData, 'valuationYear') || null,
        average_leasehold_fee: calculateAverageGeneric(selectedSoldTableData, 'leaseholdFee'),
        average_leasehold_period: calculateAverageGeneric(selectedSoldTableData, 'leaseholdPeriod'),
      }
    } else {
      return null
    }
  }, [selectedSoldTableData]);

  const adsCalculations = useMemo((): AdsSummaryResponseType | null => {
    if (selectedAdsTableData) {
      return {
        average_advertising_days: calculateAverageGeneric(selectedAdsTableData, 'advertisingDays'),
        average_ancillary_area: calculateAverageGeneric(selectedAdsTableData, 'ancillaryArea') ?? 0,
        average_construction_year: calculateAverageGeneric(selectedAdsTableData, 'constructionYear') ?? 0,
        average_distance: 0,
        average_living_area: calculateAverageGeneric(selectedAdsTableData, 'livingArea') ?? 0,
        average_monthly_fee: calculateAverageGeneric(selectedAdsTableData, 'monthlyFee') ?? 0,
        average_monthly_fee_sqm: calculateAverageMonthlyFeeSqm(selectedAdsTableData),
        average_price: calculateAverageGeneric(selectedAdsTableData, 'price') ?? 0,
        average_price_sqm: calculateAverageGeneric(selectedAdsTableData, 'priceSqm') ?? 0,
        average_price_today: calculateAverageGeneric(selectedAdsTableData, 'price') ?? 0,
        average_registered_date: calculateAverageDateTimestamp(selectedAdsTableData, 'registeredDate'),
        average_plot_area: calculateAverageGeneric(selectedAdsTableData, 'plotArea'),
        average_rooms: calculateAverageGeneric(selectedAdsTableData, 'rooms') ?? 0,
      };

    } else {
      return null;
    }
    
  }, [selectedAdsTableData]);

  return (
    <TableCalculationsContext.Provider value={{ soldCalculations, adsCalculations, selectedSoldTableData, selectedAdsTableData }}>
      {children}
    </TableCalculationsContext.Provider>
  );
};